import {
    Alert,
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    MenuItem,
    SvgIcon,
    TextField,
    ThemeProvider,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import Modal from '@mui/material/Modal';
import cn from 'classnames';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { RootState, AppDispatch } from 'redux/store';
import { loadMilestones } from 'redux/application/milestones/milestones.middleware';
import { createApp, loadApp } from 'redux/application/application.middleware';
import { createMilestones } from 'redux/application/milestones/milestones.middleware';
import { loadPrograms } from 'redux/programs/programs.middleware';
import { loadAgents } from 'redux/agents/agents.middleware';
import IconBtn from 'components/common/IconBtn';
import {
    Modal as SharedModal,
    ModalHeader as SharedModalHeader,
    Actions as SharedModalActions,
    PrimaryButton as SharedPrimaryButton,
    SecondaryButton as SharedSecondaryButton,
} from 'components/common/Modal';
import AgreementSignature from 'components/common/AgreementSignature/AgreementSignature';
import ConfirmationDialog from 'components/v2/common/confirmationDialog';
import { ReactComponent as CrossSimpleIcon } from 'assets/icons/cross-simple.svg';
import api, {
    MilestoneApp,
    MilestonePayload,
    ApplicationStatus,
    ApplicationPayload,
    AxiosResponse,
    Application,
} from 'api';
import { Template } from 'components/templates/TemplateDropdownWithActions';
import InlineTemplateEditor from 'components/templates/InlineTemplateEditor';
import TemplateDeleteConfirmation from 'components/templates/TemplateDeleteConfirmation';

import styles from './Agreement.module.scss';
import TotalServiceFee from 'views/ProviderClients/MyClients/views/Agreement/components/TotalServiceFee/TotalServiceFee';
import MilestonesListWithPayment from 'views/ProviderClients/MyClients/views/Agreement/components/MilestonesListWithPayment';
import MilestonesAndFees from 'views/ProviderClients/MyClients/views/Agreement/components/MilestonesAndFees/MilestonesAndFees';

import * as S from '../../index.styled';
import { setActiveApp } from 'redux/application/application.slice';
import { Client } from 'api/models/response/ClientList';
import { useTranslation } from 'react-i18next';
import { useWindowSize } from 'hooks/useWindowSize';
import RetainerSection from '../../components/RetainerSection/RetainerSection';

import GenericSearchableDropdown from 'components/common/Searchabledropdown/genericSearchableDropdown';
import { AgentRoles } from 'api/models/payload/AgentRoles';
import { FieldValues } from 'react-hook-form';
import { loadApplications } from 'redux/applications/applications-middleware';
import { ReactComponent as EditIcon } from 'assets/icons/B2B/border_color.svg';
import { FaTrash } from 'react-icons/fa';

import { resetMilestones } from 'redux/application/milestones/milestones.slice';
import { BiTrash } from 'react-icons/bi';
import AssessmentFee from 'views/ProviderClients/MyClients/views/Agreement/components/MilestonesAndFees/AsessmentFee';
import {
    // associateTemplateWithApplication,
    loadApplicationTemplate,
    loadTemplatesForSelection,
    deleteTemplate,
} from 'redux/retainer-templates/templates.middleware';
// import {
//     selectTemplateForAgreement,
//     setShowInlineCreator,
//     setShowInlineEditor,
//     setTemplateBeingEdited,
//     setTemplateBeingDeleted,
// } from 'redux/retainer-templates/templates.slice';
import { useToast } from 'hooks/use-toast';
import { FEATURE } from 'constant/features';
import { useHasFeature } from 'hooks/useHasFeature';
import useEnabledCountries from 'hooks/useEnabledCountries';
import appThemeV2 from 'theme/themeV2';
import { chosenCountryName, countryNameOptions } from 'utils/countries';
import { isFeatureNotEnabledError } from 'utils/features';
interface CreateAgreementModalProps {
    onClose?: () => void;
}

type TemplateMilestoneSource = {
    title?: string;
    name?: string;
    description?: string;
    amount?: number;
};

type TemplatePricingConfig = {
    milestone?: { milestones?: TemplateMilestoneSource[] };
    hybrid?: { milestones?: TemplateMilestoneSource[] };
    fixed?: { baseFeeAmount?: number; description?: string };
};

const getMilestonesTotal = (milestones: Array<{ price: string }>) =>
    milestones
        .reduce((acc, milestone) => {
            const numericValue = String(milestone.price)
                .trim()
                .replace(/^[A-Z]{3}\s+/, '');
            return acc + (Number(numericValue) || 0);
        }, 0)
        .toFixed(2);

const CreateAgreementModalInner = ({ onClose }: CreateAgreementModalProps) => {
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { codes: enabledCountryCodes } = useEnabledCountries();
    const { t } = useTranslation();
    const { toast } = useToast();
    const canManageTemplates = useHasFeature(FEATURE.RETAINER_TEMPLATES);
    const { id: candidateID, appID } = useParams();
    const [currentMilestones, setCurrentMilestones] = useState<MilestonePayload[]>([]);

    // Log when currentMilestones changes to track re-renders
    useEffect(() => {
        console.log('🎨 CURRENT MILESTONES CHANGED:', currentMilestones.length, 'milestones');
    }, [currentMilestones]);
    const [isFullscreenModeContractModal, setFullscreenModeContractModal] = useState<boolean>(true);
    const { entity: activeCase } = useSelector((state: RootState) => state.application);

    const milestonesData = useSelector((state: RootState) => state.milestonesApp.list);
    const newMilestone = useSelector((state: RootState) => state.milestonesApp.newMilestone);
    const [currentBrowserClient] = useState<Client>();

    const [hasTableErrors, setTableHasErrors] = useState<boolean>(false);
    const dispatch = useDispatch<AppDispatch>();
    const [width, height] = useWindowSize();
    const [mobile, setMobile] = useState(false);
    const theme = useTheme();
    const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));
    const [currency, setCurrency] = useState<string>('USD');
    const [isContractModalOpen, setContractModalOpen] = useState<boolean>(false);

    // const { entity: consultant } = useSelector((state: RootState) => state.user);
    const params = useParams();
    const isRetainerView = params['*']?.includes('/view');
    const isRetainerEditView = params['*']?.endsWith('/edit');
    const isNew = params['*']?.includes('/agreement');
    const isEdit = params['*']?.includes('/edit');
    const availableCountryOptions = useMemo(
        () => countryNameOptions(enabledCountryCodes, activeCase?.country),
        [activeCase?.country, enabledCountryCodes]
    );
    const [casePrograms, setCasePrograms] = useState<{ label: string; value: number }[]>([]);
    const { applications: clientApps } = useSelector((state: RootState) => state.applications);
    const [selectedCaseProgram, setSelectedCaseProgram] = useState('');

    const [selectedCaseCountry, setSelectedCaseCountry] = useState(
        () => chosenCountryName(enabledCountryCodes) ?? ''
    );
    const [selectedProgram, setSelectedProgram] = useState('');
    const [selectedAgent, setSelectedAgent] = useState('');
    const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
    const [notifyClient, setNotifyClient] = useState(false);

    // Redux selectors
    const { associatedTemplate, templatesForSelection: allTemplates } = useSelector(
        (state: RootState) => state.templates
    );
    const { list: countryBasedProgramList } = useSelector((state: RootState) => state.programs);
    const { list: visaAgents, isLoaded: agentsLoaded } = useSelector(
        (state: RootState) => state.agents
    );

    const navigate = useNavigate();
    const [selectedNewRetainerType] = useState<'visa' | 'assessment'>('visa');

    const [openConfirmationDialod, setOpenConfirmationDialog] = useState<boolean>(false);
    const openCloseConfirmationDialog = async (appId?: string) => {
        setOpenConfirmationDialog((prevState) => !prevState);
    };

    // Inline template CRUD modal states
    const [showInlineTemplateEditor, setShowInlineTemplateEditor] = useState(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [showTemplateChangeWarning, setShowTemplateChangeWarning] = useState(false);
    const [templateToEdit, setTemplateToEdit] = useState<Template | null>(null);
    const [templateToDelete, setTemplateToDelete] = useState<Template | null>(null);
    const [pendingTemplateSelection, setPendingTemplateSelection] = useState<string | null>(null);

    // Track if user has edited milestones after template selection
    const [userHasEditedMilestones, setUserHasEditedMilestones] = useState(false);
    const [originalTemplateMilestones, setOriginalTemplateMilestones] = useState<
        MilestonePayload[]
    >([]);

    // Track if we're loading milestones from template to prevent marking as edited
    const isLoadingFromTemplateRef = useRef(false);

    // Track if we've already synced milestones from Redux
    const hasSyncedMilestonesRef = useRef(false);

    // Track which appID we've loaded milestones for to prevent duplicate loads
    const loadedMilestonesForAppIdRef = useRef<string | null>(null);

    // Track which appID we've loaded template for to prevent duplicate loads
    const loadedTemplateForAppIdRef = useRef<string | null>(null);

    const currentMilestonesRef = useRef<MilestonePayload[]>(currentMilestones);
    const originalTemplateMilestonesRef = useRef<MilestonePayload[]>(originalTemplateMilestones);
    currentMilestonesRef.current = currentMilestones;
    originalTemplateMilestonesRef.current = originalTemplateMilestones;

    // Track agent signature validation error
    const [agentSignatureError, setAgentSignatureError] = useState<string>('');

    useEffect(() => {
        console.log('🎯 Milestones Load Effect:', {
            candidateID,
            appID,
            loadedFor: loadedMilestonesForAppIdRef.current,
            willLoad: candidateID && appID && loadedMilestonesForAppIdRef.current !== appID,
        });

        if (candidateID && appID) {
            if (loadedMilestonesForAppIdRef.current !== appID) {
                loadedMilestonesForAppIdRef.current = appID;
                hasSyncedMilestonesRef.current = false;
                setCurrentMilestones([]);
                dispatch(resetMilestones());
                dispatch(loadMilestones(appID.toString()));
            } else {
                console.log('⏭️ SKIPPING milestone load (already loaded for appID:', appID + ')');
            }
        } else if (!appID) {
            console.log('🔄 RESETTING milestone tracking (no appID)');
            // Reset when no appID
            loadedMilestonesForAppIdRef.current = null;
            hasSyncedMilestonesRef.current = false;
        }
    }, [candidateID, appID, dispatch]);

    useEffect(() => {
        if (appID) {
            dispatch(loadApp(appID));
            return;
        }
        dispatch(setActiveApp(null));
    }, [appID, dispatch]);

    // Reset form when appID changes to null
    useEffect(() => {
        if (!appID) {
            setSelectedProgram('');
            setSelectedCaseCountry(chosenCountryName(enabledCountryCodes) ?? '');
            setSelectedAgent('');
            setSelectedCaseProgram('');
            setSelectedTemplateId('');
            setCurrentMilestones([]);
            setAgentSignatureError('');
            hasSyncedMilestonesRef.current = false;
            dispatch(resetMilestones());
            dispatch(setActiveApp(null));
        }
    }, [appID, dispatch, enabledCountryCodes]);

    // Auto-populate from activeCase for new applications
    useEffect(() => {
        // Only auto-populate when:
        // 1. We have an activeCase
        // 2. It's a new application
        // 3. No template is selected (avoid overwriting template data)
        // 4. We have an appID
        if (activeCase && isNew && !selectedTemplateId && appID) {
            const programName = activeCase?.program_name ?? '';
            const country = activeCase?.country || chosenCountryName(enabledCountryCodes) || '';
            const consultantId =
                activeCase?.consultant_id != null ? String(activeCase.consultant_id) : '';

            // Only update if values have changed to prevent unnecessary re-renders
            setSelectedProgram((prev) => (prev !== programName ? programName : prev));
            setSelectedCaseCountry((prev) => (prev !== country ? country : prev));
            setSelectedAgent((prev) => (prev !== consultantId ? consultantId : prev));
        }
    }, [activeCase, appID, enabledCountryCodes, isNew, selectedTemplateId]);

    // Memoize formatted milestones to prevent unnecessary conversions
    const formattedMilestones = useMemo(() => {
        console.log('🔨 Formatting milestones:', {
            count: milestonesData?.length || 0,
            hasData: !!milestonesData,
        });

        if (!milestonesData || milestonesData.length === 0) {
            console.log('⚠️ No milestones data to format');
            return [];
        }

        const formatted = milestonesData.map((m) => ({
            id: m.id,
            name: m.name,
            description: m.description || '',
            price: String(m.price),
            currency: m.currency || 'USD',
            application_id: m.application_id,
            payment_status: m.payment_status || 'PENDING',
            created_at: m.created_at,
            hubspot_id: m.hubspot_id,
            status: m.status || 'PENDING',
            payment_id: m.payment_id,
        }));

        console.log('✅ Formatted milestones:', formatted.length);
        return formatted;
    }, [milestonesData]);

    // Sync loaded milestones from Redux to form state (only when explicitly loaded)
    useEffect(() => {
        console.log('💾 Milestones Sync Effect:', {
            formattedCount: formattedMilestones.length,
            currentCount: currentMilestones.length,
            isLoadingFromTemplate: isLoadingFromTemplateRef.current,
            hasSynced: hasSyncedMilestonesRef.current,
            willSync:
                formattedMilestones.length > 0 &&
                !isLoadingFromTemplateRef.current &&
                !hasSyncedMilestonesRef.current,
        });

        // Only sync if:
        // 1. We have milestones data
        // 2. We're not loading from template
        // 3. We haven't already synced
        // 4. The formatted milestones have changed
        if (
            formattedMilestones.length > 0 &&
            !isLoadingFromTemplateRef.current &&
            !hasSyncedMilestonesRef.current &&
            appID &&
            String(formattedMilestones[0].application_id) === appID
        ) {
            console.log(
                '🔄 SYNCING milestones from Redux to form state:',
                formattedMilestones.length,
                'milestones'
            );
            setCurrentMilestones(formattedMilestones);
            hasSyncedMilestonesRef.current = true;
            console.log('✅ Sync complete, hasSynced flag set to true');
        } else {
            console.log('⏭️ SKIPPING sync');
        }
    }, [formattedMilestones, currentMilestones.length, appID]);

    useEffect(() => {
        const convertedData = clientApps.map((app) => ({
            value: app.id,
            label: app.program_name,
        }));
        setCasePrograms(convertedData);
    }, [clientApps]);

    useEffect(() => {
        if (visaAgents && isNew) {
            assignAgents(Number(selectedAgent));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visaAgents, isNew, selectedAgent]);

    const createNewCase = async (data: FieldValues) => {
        if (candidateID) {
            const createPayload: ApplicationPayload = {
                candidate_id: Number(candidateID),
                program_name: data.visaProgram,
                country: data.country,
                retainer_template_id: selectedTemplateId ? Number(selectedTemplateId) : undefined,
                ...(data.consultant_id ? { consultant_id: Number(data.consultant_id) } : {}),
            };
            const resp = await dispatch(createApp(createPayload));
            const createdCase = (resp.payload as AxiosResponse).data;

            await dispatch(loadApplications(candidateID));
            return createdCase;
        }
        return null;
    };

    const assignAgents = async (visaAgent: number) => {
        if (activeCase) {
            if (visaAgent) {
                try {
                    const payload = {
                        agent_id: visaAgent,
                        application_id: activeCase.id,
                    };
                    await api.agents.assignAgent(AgentRoles.CONSULTANT, payload);
                } catch (e) {
                    console.error(e);
                }
            }
        }
    };

    // Load programs when country changes
    useEffect(() => {
        if (candidateID && selectedCaseCountry) {
            dispatch(loadPrograms({ userId: candidateID, country: selectedCaseCountry }));
        }
    }, [candidateID, selectedCaseCountry, dispatch]);

    // Load agents once on mount
    useEffect(() => {
        if (!agentsLoaded) {
            dispatch(loadAgents());
        }
    }, [agentsLoaded, dispatch]);

    // Load ALL templates at Step 1 via Redux
    useEffect(() => {
        if (selectedNewRetainerType === 'visa') {
            dispatch(loadTemplatesForSelection({ type: 'individual' }));
        }
    }, [selectedNewRetainerType, dispatch]);

    // Clear agent signature errors when not in visa flow
    useEffect(() => {
        if (selectedNewRetainerType !== 'visa') {
            setAgentSignatureError('');
        }
    }, [selectedNewRetainerType]);

    // Auto-populate country/program/agent and load milestones from selected template
    useEffect(() => {
        if (selectedTemplateId && allTemplates.length > 0) {
            const selectedTemplate = allTemplates.find((t) => t.id === selectedTemplateId);
            if (selectedTemplate) {
                console.log('📝 Auto-populating from template:', selectedTemplate);

                // A template cannot override the provider's enabled markets.
                if (
                    selectedTemplate.country &&
                    availableCountryOptions.includes(selectedTemplate.country)
                ) {
                    setSelectedCaseCountry(selectedTemplate.country);
                }

                // Always update program when template is selected
                if (selectedTemplate.program) {
                    setSelectedProgram(selectedTemplate.program);
                }

                // Always update agent when template is selected
                if (selectedTemplate.assignedAgentId) {
                    setSelectedAgent(String(selectedTemplate.assignedAgentId));
                }

                // When editing (appID exists), milestones come from API via sync effect — don't overwrite with template
                const shouldLoadMilestonesFromTemplate = !appID && selectedTemplate.pricingConfig;
                if (shouldLoadMilestonesFromTemplate && selectedTemplate.pricingConfig) {
                    console.log(
                        '📊 Loading milestones from pricingConfig:',
                        selectedTemplate.pricingConfig
                    );

                    let templateMilestones: MilestonePayload[] = [];
                    const currency = selectedTemplate.defaultCurrency || 'USD';
                    const pricingConfig = selectedTemplate.pricingConfig as TemplatePricingConfig;

                    // Extract milestones - check all possible locations (milestone-based pricing takes precedence)
                    if (
                        pricingConfig.milestone?.milestones &&
                        pricingConfig.milestone.milestones.length > 0
                    ) {
                        // Milestone pricing model
                        templateMilestones = pricingConfig.milestone.milestones.map(
                            (m: TemplateMilestoneSource, index: number) => ({
                                id: index + 1,
                                name: m.title || m.name || '',
                                description: m.description || '',
                                price: `${m.amount || 0}`,
                                currency,
                                application_id: 0,
                            })
                        );
                        console.log('✅ Loaded milestones from milestone pricing');
                    } else if (
                        pricingConfig.hybrid?.milestones &&
                        pricingConfig.hybrid.milestones.length > 0
                    ) {
                        // Hybrid pricing model
                        templateMilestones = pricingConfig.hybrid.milestones.map(
                            (m: TemplateMilestoneSource, index: number) => ({
                                id: index + 1,
                                name: m.title || m.name || '',
                                description: m.description || '',
                                price: `${m.amount || 0}`,
                                currency,
                                application_id: 0,
                            })
                        );
                        console.log('✅ Loaded milestones from hybrid pricing');
                    } else if (
                        pricingConfig.fixed?.baseFeeAmount &&
                        pricingConfig.fixed.baseFeeAmount > 0
                    ) {
                        // Fixed pricing model with a base fee
                        templateMilestones = [
                            {
                                id: 1,
                                name: 'Service Fee',
                                description:
                                    pricingConfig.fixed.description || 'Full service retainer',
                                price: `${pricingConfig.fixed.baseFeeAmount}`,
                                currency,
                                application_id: 0,
                            },
                        ];
                        console.log('✅ Loaded milestone from fixed pricing');
                    }

                    console.log('✅ Final template milestones:', templateMilestones);

                    if (templateMilestones.length > 0) {
                        isLoadingFromTemplateRef.current = true;
                        setCurrentMilestones(templateMilestones);
                        setOriginalTemplateMilestones(templateMilestones);
                        setCurrency(currency);
                        setUserHasEditedMilestones(false);
                        const timeoutId = setTimeout(() => {
                            isLoadingFromTemplateRef.current = false;
                        }, 500);
                        return () => clearTimeout(timeoutId);
                    }
                }
            }
        }
    }, [allTemplates, appID, availableCountryOptions, selectedTemplateId]);

    // Load associated template when application loads
    useEffect(() => {
        console.log('📋 Template Load Effect:', {
            appID,
            loadedFor: loadedTemplateForAppIdRef.current,
            willLoad: appID && loadedTemplateForAppIdRef.current !== appID,
        });

        if (appID) {
            // Only load if we haven't already loaded for this appID
            if (loadedTemplateForAppIdRef.current !== appID) {
                console.log('✅ LOADING APPLICATION TEMPLATE for appID:', appID);
                loadedTemplateForAppIdRef.current = appID;
                dispatch(loadApplicationTemplate(Number(appID)));
            } else {
                console.log('⏭️ SKIPPING template load (already loaded for appID:', appID + ')');
            }
        } else {
            console.log('🔄 RESETTING template tracking (no appID)');
            loadedTemplateForAppIdRef.current = null;
        }
    }, [appID, dispatch]);

    // Set selected template when associated template loads (edit mode only)
    useEffect(() => {
        if (associatedTemplate && appID) {
            setSelectedTemplateId(associatedTemplate.id.toString());
        }
    }, [associatedTemplate, appID]);

    const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);

    const handleDeleteOpen = async () => {
        setDeleteModalOpen(true);
    };

    const handleDeleteClose = () => setDeleteModalOpen(false);

    const handleDeleteConfirmed = async () => {
        if (activeCase) {
            try {
                await api.assessment.deleteApplication(activeCase.id);
                handleDeleteClose();
                window.location.reload();
            } catch (error) {
                console.error('Failed to delete application:', error);
            }
        }
    };

    // Template CRUD handlers
    const handleConfirmDeleteTemplate = async () => {
        if (templateToDelete) {
            try {
                await dispatch(deleteTemplate(Number(templateToDelete.id))).unwrap();
                toast({
                    title: t('success', { lng: language }) || 'Success',
                    description:
                        t('templateDeleted', { lng: language }) || 'Template deleted successfully',
                });

                // Refresh template list via Redux
                dispatch(loadTemplatesForSelection({ type: 'individual' }));

                // Clear selection if deleted template was selected
                if (selectedTemplateId === templateToDelete.id) {
                    setSelectedTemplateId('');
                }
            } catch (error) {
                if (isFeatureNotEnabledError(error)) {
                    return;
                }
                toast({
                    title: t('error', { lng: language }) || 'Error',
                    description:
                        t('failedToDeleteTemplate', { lng: language }) ||
                        'Failed to delete template',
                    variant: 'destructive',
                });
            } finally {
                setShowDeleteConfirmation(false);
                setTemplateToDelete(null);
            }
        }
    };

    const handleTemplateSelect = (templateId: string) => {
        // Check if user has edited milestones
        if (userHasEditedMilestones && selectedTemplateId && templateId !== selectedTemplateId) {
            // Show warning
            setPendingTemplateSelection(templateId);
            setShowTemplateChangeWarning(true);
        } else {
            // Directly select template
            setSelectedTemplateId(templateId);
            setUserHasEditedMilestones(false);
        }
    };

    const handleConfirmTemplateChange = () => {
        if (pendingTemplateSelection) {
            setSelectedTemplateId(pendingTemplateSelection);
            setUserHasEditedMilestones(false);
            setCurrentMilestones([]);
            setPendingTemplateSelection(null);
        }
        setShowTemplateChangeWarning(false);
    };

    const handleCancelTemplateChange = () => {
        setPendingTemplateSelection(null);
        setShowTemplateChangeWarning(false);
    };

    // Validate agent has a default signature
    const validateAgentSignature = async (agentId: number) => {
        try {
            const response = await api.signatures.getAgentDefaultSignature(agentId);

            if (!response.data) {
                // No default signature exists for this agent
                const errorMessage =
                    t('noDefaultSignature', { lng: language }) ||
                    'No default signature is created for the selected agent. Please ask the agent to create a signature first.';
                setAgentSignatureError(errorMessage);
                throw new Error('No default signature');
            }

            // Clear error if signature exists
            setAgentSignatureError('');
            return response.data;
        } catch (error) {
            console.error('Agent signature validation failed:', error);
            // Error is already set in state
            throw error;
        }
    };

    useEffect(() => {
        // Only set currency from milestonesData if we have existing milestones
        // Don't override currency set from template selection
        if (!isNew && milestonesData && milestonesData.length > 0) {
            setCurrency(milestonesData[0].currency);
        } else if (!isNew && (!milestonesData || milestonesData.length === 0)) {
            // Default to USD only if no template currency was set
            setCurrency('USD');
        }
    }, [appID, milestonesData, isNew]);

    useEffect(() => {
        if (width >= 769) {
            setMobile(false);
        }
        if (width < 769) {
            setMobile(true);
        }
    }, [width, height]);

    useEffect(() => {
        if (candidateID) {
            //  getClient(Number(candidateID));
        }
    }, [candidateID]);

    const createOfferWithMilestonesAttached = async () => {
        const respActiveCase = await createCase();
        if (respActiveCase?.id) {
            await createOffer(respActiveCase);
        }
    };

    const updateCase = async () => {
        if (appID && activeCase) {
            await assignAgents(Number(selectedAgent));
            await createOffer(activeCase);
        }
    };

    const createCase = async () => {
        if (selectedCaseCountry !== '' && selectedProgram !== '' && !isRetainerEditView) {
            return await createNewCase({
                candidate_id: candidateID,
                visaProgram: selectedProgram,
                country: selectedCaseCountry,
                consultant_id: selectedAgent || undefined,
            });
        }
        return null;
    };

    const createOffer = async (data?: Application) => {
        const tempActiveCase = data || activeCase;
        if (!tempActiveCase?.id) {
            console.error('Cannot create offer: active case ID is missing');
            return;
        }

        // Clean milestones data before sending to backend
        const cleanedMilestones = currentMilestones.map((milestone) => {
            const priceStr = String(milestone.price).trim();
            const currencyMatch = priceStr.match(/^([A-Z]{3})\s+/);
            const extractedCurrency = currencyMatch
                ? currencyMatch[1]
                : milestone.currency || currency || 'USD';
            const numericPrice = priceStr.replace(/^[A-Z]{3}\s+/, '');

            return {
                ...milestone,
                price: numericPrice,
                currency: extractedCurrency,
            };
        });

        const payload = {
            appID: tempActiveCase.id.toString(),
            payload: cleanedMilestones,
            notifyClient,
        };
        await dispatch(createMilestones(payload));
        await dispatch(loadApp(tempActiveCase.id.toString()));
        await dispatch(loadMilestones(tempActiveCase.id.toString()));
        openCloseConfirmationDialog();
        onClose?.();
    };

    const handleMilestoneListUpdate = useCallback(
        (list: MilestonePayload[]) => {
            if (isLoadingFromTemplateRef.current) {
                return;
            }
            const newListStr = JSON.stringify(list);
            const currentListStr = JSON.stringify(currentMilestonesRef.current);
            if (newListStr !== currentListStr) {
                setCurrentMilestones(list);
                if (selectedTemplateId && originalTemplateMilestonesRef.current.length > 0) {
                    const originalStr = JSON.stringify(originalTemplateMilestonesRef.current);
                    if (newListStr !== originalStr) {
                        setUserHasEditedMilestones(true);
                    }
                }
            }
        },
        [selectedTemplateId]
    );

    const handleMilestoneErrors = useCallback((value: boolean) => {
        setTableHasErrors(value);
    }, []);

    const memoizedMilestones: MilestoneApp[] = useMemo(() => {
        if (isNew) {
            return currentMilestones.map((m, index) => ({
                id: typeof m.id === 'number' ? m.id : index + 1,
                name: m.name,
                description: m.description || '',
                price: m.price,
                currency: m.currency || 'USD',
                application_id: m.application_id ?? 0,
                payment_status: '',
                created_at: new Date().toISOString(),
                hubspot_id: null,
                status: '',
                payment_id: null,
            }));
        }
        return milestonesData;
    }, [isNew, currentMilestones, milestonesData]);

    // ApplicationStatus.OFFER_REJECTED status doesn't have own template for Agreement
    // and for Documents page (Only VisaProgram page is available for this status)
    const getTemplate = () => {
        if (!appID) return getNewRetainerAgreementTemplate();
        if (!activeCase) return getNewRetainerAgreementTemplate();
        if (candidateID && appID) {
            // if (activeCase.program_name === 'Open Case') {
            //     return getOpenCaseTemplate();
            // }
            if (activeCase.program_name) {
                return getNewRetainerAgreementTemplate();
            }
            if (activeCase.date_agreement_signed) {
                return getSignedAgreementTemplate();
            }
            return getNewRetainerAgreementTemplate();
        }
        return null;
    };

    const getNewRetainerAgreementTemplate = () => {
        return (
            <>
                {isRetainerView && !isRetainerEditView ? (
                    <>
                        <RetainerSection title="1: Select Case">
                            <GenericSearchableDropdown
                                label={t('selectCase', { lng: language })}
                                selectedType={selectedCaseProgram}
                                handleSelectedType={(value) => {
                                    setSelectedCaseProgram(value);
                                }}
                                dropdownMenuPrograms={casePrograms}
                                purple={true}
                                style={{ marginLeft: 'auto' }}
                                handleNavigation={true}
                            />
                        </RetainerSection>
                        {activeCase?.program_name && (
                            <>
                                <div style={{ marginLeft: 'auto' }}>
                                    <IconBtn
                                        style={{
                                            padding: '0px',
                                            height: '20px',
                                            width: '20px',
                                            marginRight: '10px',
                                        }}
                                        onClick={() => {
                                            navigate('edit');
                                        }}
                                    >
                                        <EditIcon fill="#282828" />
                                    </IconBtn>
                                    <IconBtn
                                        style={{
                                            padding: '0px',
                                            height: '20px',
                                            width: '20px',
                                            marginRight: '10px',
                                        }}
                                        onClick={() => {
                                            handleDeleteOpen();
                                        }}
                                    >
                                        <FaTrash />
                                    </IconBtn>
                                    {isRetainerView &&
                                        activeCase &&
                                        activeCase.date_offer_sent &&
                                        activeCase.date_agreement_signed && (
                                            <IconButton
                                                style={{
                                                    marginLeft: '15px',
                                                    color: '#282828',
                                                }}
                                                onClick={handleDeleteOpen}
                                            >
                                                <SvgIcon
                                                    style={{
                                                        width: '15',
                                                        height: '16.7px',
                                                    }}
                                                    viewBox="3 5 18 16.7"
                                                    component={BiTrash}
                                                    color="error"
                                                    htmlColor="black"
                                                />
                                            </IconButton>
                                        )}
                                </div>

                                <div className={agreementModalStyle}>
                                    <div className={styles['modal-contract__btn-wrapper']}>
                                        <IconBtn
                                            style={{ height: '24px', width: '24px' }}
                                            onClick={openCloseContractModal}
                                        >
                                            <CrossSimpleIcon className={styles['close-icon']} />
                                        </IconBtn>
                                    </div>
                                    {/* <AgreementSignature
                                        appID={activeCase ? activeCase.id.toString() : null}
                                        appStatus={activeCase ? activeCase.status : null}
                                        onFullscreenMode={handleAgreementFullscreenMode}
                                        onUserSignedAgreement={() => {
                                            console.log('adf');
                                        }}
                                    /> */}
                                </div>
                                <SharedModal
                                    open={isDeleteModalOpen}
                                    onClose={handleDeleteClose}
                                    size="sm"
                                    ariaLabel={t('confirm', { lng: language })}
                                >
                                    <SharedModalHeader
                                        title={t(
                                            'Are you sure that you want to delete this agreement?',
                                            { lng: language }
                                        )}
                                    />
                                    <SharedModalActions>
                                        <SharedSecondaryButton
                                            type="button"
                                            onClick={handleDeleteClose}
                                        >
                                            {t('cancel', { lng: language })}
                                        </SharedSecondaryButton>
                                        <SharedPrimaryButton
                                            type="button"
                                            onClick={handleDeleteConfirmed}
                                        >
                                            {t('confirm', { lng: language })}
                                        </SharedPrimaryButton>
                                    </SharedModalActions>
                                </SharedModal>
                            </>
                        )}
                    </>
                ) : (
                    <Dialog
                        open
                        onClose={
                            openConfirmationDialod || showTemplateChangeWarning
                                ? undefined
                                : onClose
                        }
                        fullWidth
                        maxWidth="md"
                        fullScreen={isSmDown}
                        scroll="paper"
                        sx={{ '& .MuiDialog-paper': { maxWidth: 720 } }}
                    >
                        <DialogTitle>
                            {t('createAndReviewRetainerAgreement', { lng: language })}
                            {/* <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                {t('createAndReviewRetainerAgreementDescription', {
                                    lng: language,
                                })}
                            </Typography> */}
                        </DialogTitle>
                        <DialogContent dividers>
                            {agentSignatureError ? (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    {agentSignatureError}
                                </Alert>
                            ) : null}

                            {selectedNewRetainerType === 'visa' ? (
                                <>
                                    <Box
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: {
                                                xs: '1fr',
                                                sm: 'repeat(2, minmax(0, 1fr))',
                                            },
                                            gap: { xs: 1.5, sm: 2 },
                                            width: '100%',
                                        }}
                                    >
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label={t('selectRetainerTemplate', {
                                                lng: language,
                                            })}
                                            value={selectedTemplateId}
                                            onChange={(e) => handleTemplateSelect(e.target.value)}
                                            disabled={isRetainerEditView}
                                        >
                                            {allTemplates.map((template) => (
                                                <MenuItem key={template.id} value={template.id}>
                                                    {template.name}
                                                </MenuItem>
                                            ))}
                                        </TextField>

                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label={t('country', { lng: language })}
                                            value={selectedCaseCountry}
                                            onChange={(e) => setSelectedCaseCountry(e.target.value)}
                                            disabled={isRetainerEditView}
                                        >
                                            {availableCountryOptions.map((country) => (
                                                <MenuItem key={country} value={country}>
                                                    {country}
                                                </MenuItem>
                                            ))}
                                        </TextField>

                                        <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
                                            <TextField
                                                select
                                                fullWidth
                                                size="small"
                                                label={t('service', { lng: language })}
                                                value={selectedProgram}
                                                onChange={(e) => setSelectedProgram(e.target.value)}
                                                disabled={isRetainerEditView}
                                                slotProps={{
                                                    select: {
                                                        renderValue: (value) => {
                                                            const selected = (
                                                                countryBasedProgramList as Array<{
                                                                    value: string;
                                                                    label: string;
                                                                    display_color?: string;
                                                                }>
                                                            ).find((opt) => opt.value === value);
                                                            return (
                                                                <span
                                                                    style={{
                                                                        color:
                                                                            selected?.display_color ||
                                                                            'inherit',
                                                                    }}
                                                                >
                                                                    {selected?.label ?? ''}
                                                                </span>
                                                            );
                                                        },
                                                    },
                                                }}
                                            >
                                                {(
                                                    countryBasedProgramList as Array<{
                                                        value: string;
                                                        label: string;
                                                        display_color?: string;
                                                    }>
                                                ).map((opt) => (
                                                    <MenuItem
                                                        key={opt.value}
                                                        value={opt.value}
                                                        sx={{
                                                            color: opt.display_color || 'inherit',
                                                        }}
                                                    >
                                                        {opt.label}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        </Box>

                                        <Box
                                            sx={{
                                                gridColumn: { sm: '1 / -1' },
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 2,
                                                flexWrap: 'wrap',
                                            }}
                                        >
                                            <TextField
                                                select
                                                fullWidth
                                                size="small"
                                                label={t('selectRetainerAgent', { lng: language })}
                                                value={selectedAgent}
                                                sx={{ flex: '1 1 220px', minWidth: 0 }}
                                                onChange={async (e) => {
                                                    const value = e.target.value;
                                                    setSelectedAgent(value);
                                                    if (!value) {
                                                        setAgentSignatureError('');
                                                        return;
                                                    }
                                                    try {
                                                        await validateAgentSignature(Number(value));
                                                    } catch {
                                                        // Error shown inline
                                                    }
                                                }}
                                            >
                                                <MenuItem value="">
                                                    {t('none', {
                                                        lng: language,
                                                        defaultValue: 'None',
                                                    })}
                                                </MenuItem>
                                                {visaAgents.map(
                                                    (agent: { value: string; label: string }) => (
                                                        <MenuItem
                                                            key={agent.value}
                                                            value={agent.value}
                                                        >
                                                            {agent.label}
                                                        </MenuItem>
                                                    )
                                                )}
                                            </TextField>
                                            <FormControlLabel
                                                sx={{
                                                    ml: 0,
                                                    mr: 0,
                                                    whiteSpace: 'nowrap',
                                                    flex: '0 0 auto',
                                                }}
                                                control={
                                                    <Checkbox
                                                        checked={notifyClient}
                                                        onChange={(e) =>
                                                            setNotifyClient(e.target.checked)
                                                        }
                                                        name="notifyClient"
                                                    />
                                                }
                                                label={t('notifyClient', {
                                                    lng: language,
                                                    defaultValue: 'Notify client',
                                                })}
                                            />
                                        </Box>
                                    </Box>

                                    {activeCase?.status === ApplicationStatus.OFFER && (
                                        <Alert severity="info" sx={{ mb: 2, mt: 2 }}>
                                            {t('yourOfferSentTo', { lng: language })}{' '}
                                            {currentBrowserClient?.firstname}{' '}
                                            {currentBrowserClient?.lastname}
                                        </Alert>
                                    )}

                                    <Box sx={{ mt: 2 }}>
                                        <MilestonesAndFees
                                            v2Style
                                            milestones={memoizedMilestones}
                                            editMode={true}
                                            isCreating={newMilestone.isLoading}
                                            onCurrentMilestonesList={handleMilestoneListUpdate}
                                            onHasErrors={handleMilestoneErrors}
                                        />
                                    </Box>
                                </>
                            ) : (
                                <AssessmentFee handleClose={onClose} />
                            )}
                        </DialogContent>
                        {selectedNewRetainerType === 'visa' && (
                            <DialogActions
                                sx={{
                                    px: 3,
                                    py: 2,
                                    gap: 1,
                                    flexWrap: 'wrap',
                                    justifyContent: 'flex-end',
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ mr: 'auto', fontWeight: 600 }}
                                >
                                    {t('totalServiceFee', { lng: language })}:{' '}
                                    <Box
                                        component="span"
                                        sx={{ color: 'text.primary', fontSize: '1rem' }}
                                    >
                                        {getMilestonesTotal(currentMilestones)}
                                    </Box>
                                </Typography>
                                <Button
                                    variant="outlined"
                                    onClick={onClose}
                                    sx={{ textTransform: 'none' }}
                                >
                                    {t('cancel', { lng: language })}
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={() => {
                                        isEdit
                                            ? setOpenConfirmationDialog(true)
                                            : createOfferWithMilestonesAttached();
                                    }}
                                    disabled={hasTableErrors || currentMilestones.length === 0}
                                    sx={{ textTransform: 'none' }}
                                >
                                    {t('saveChanges', { lng: language })}
                                </Button>
                            </DialogActions>
                        )}

                        {openConfirmationDialod ? (
                            <ConfirmationDialog
                                title={t('pleaseNote', { lng: language })}
                                content={t('overwriteMilestonesWarning', { lng: language })}
                                onClose={openCloseConfirmationDialog}
                                onConfirm={() => updateCase()}
                            />
                        ) : null}

                        {canManageTemplates && (
                            <InlineTemplateEditor
                                open={showInlineTemplateEditor}
                                template={templateToEdit}
                                onClose={() => {
                                    setShowInlineTemplateEditor(false);
                                    setTemplateToEdit(null);
                                }}
                                onSuccess={async () => {
                                    dispatch(loadTemplatesForSelection({ type: 'individual' }));
                                }}
                                mobile={mobile}
                            />
                        )}

                        {canManageTemplates && (
                            <TemplateDeleteConfirmation
                                open={showDeleteConfirmation}
                                template={templateToDelete}
                                isCurrentlySelected={templateToDelete?.id === selectedTemplateId}
                                onClose={() => {
                                    setShowDeleteConfirmation(false);
                                    setTemplateToDelete(null);
                                }}
                                onConfirm={handleConfirmDeleteTemplate}
                                mobile={mobile}
                            />
                        )}

                        {showTemplateChangeWarning ? (
                            <ConfirmationDialog
                                title={t('confirmTemplateChange', { lng: language })}
                                content={t('changingTemplateWillResetMilestones', {
                                    lng: language,
                                })}
                                onClose={handleCancelTemplateChange}
                                onConfirm={handleConfirmTemplateChange}
                            />
                        ) : null}
                    </Dialog>
                )}
            </>
        );
    };

    const openCloseContractModal = () => {
        setContractModalOpen((state) => !state);
    };

    const handleAgreementFullscreenMode = (value: boolean) => {
        setFullscreenModeContractModal(value);
    };

    // Styles
    const agreementModalWrapperStyle = cn(styles['modal-contract-wrapper'], {
        [styles['modal-contract-wrapper_fullscreen']]: isFullscreenModeContractModal,
    });

    const agreementModalStyle = cn(styles['modal-contract'], {
        [styles['modal-contract_border-none']]: isFullscreenModeContractModal,
    });

    const getSignedAgreementTemplate = () => {
        return (
            <>
                {activeCase && (
                    <>
                        <section className={styles.milestones}>
                            <h3 className={styles['milestones__title']}>
                                {activeCase.program_name}
                            </h3>
                            <MilestonesListWithPayment
                                milestones={milestonesData}
                                isAgreementSigned={true}
                            />
                        </section>
                        <S.SignedRetainerAgreementTemplateFooter>
                            <TotalServiceFee milestones={milestonesData} />
                        </S.SignedRetainerAgreementTemplateFooter>
                    </>
                )}

                <Modal
                    sx={{ zIndex: '10000' }}
                    open={isContractModalOpen}
                    onClose={openCloseContractModal}
                >
                    <div className={agreementModalWrapperStyle}>
                        <div className={agreementModalStyle}>
                            {/* <div className={styles['modal-contract__btn-wrapper']}>
                                <IconBtn
                                    style={{ height: '24px', width: '24px' }}
                                    onClick={openCloseContractModal}>
                                    <CrossSimpleIcon className={styles['close-icon']} />
                                </IconBtn>
                            </div> */}
                            {activeCase ? activeCase.id : '----'}
                            <AgreementSignature
                                appID={activeCase ? activeCase.id.toString() : null}
                                onFullscreenMode={handleAgreementFullscreenMode}
                                onUserSignedAgreement={() => {
                                    console.log('');
                                }}
                            />
                        </div>
                    </div>
                </Modal>
            </>
        );
    };

    return <>{getTemplate()}</>;
};

const CreateAgreementModal = ({ onClose }: CreateAgreementModalProps) => (
    <ThemeProvider theme={appThemeV2}>
        <CreateAgreementModalInner onClose={onClose} />
    </ThemeProvider>
);

export default CreateAgreementModal;
